import { useState, useEffect, type FormEvent } from 'react';
import {
  sgpInvoiceSecondCopy, sgpInvoicePix, sgpInvoiceSend,
  sgpRebootCpe, sgpDisconnectRadius, sgpCreateTicket,
  sgpSettleInvoice, sgpReverseInvoice, sgpCancelInvoice,
  sgpUpdateContractStatus, sgpAuthorizeOnu, sgpDeauthorizeOnu,
  sgpReverseStock, sgpDeleteCard, sgpDeleteRecurringCharge,
  sgpDeleteGenericService, sgpDeleteCustomerDocument,
  testRadius,
} from '../api/sgp';
import { getCompanies } from '../api/companies';
import { useAuth } from '../context/AuthContext';
import type { Customer, Company } from '../types';

type OperationTab = 'financial' | 'technical' | 'critical' | 'radius';

interface SgpOperationsPanelProps {
  customer: Customer;
  onClose: () => void;
}

function formatCpfCnpj(doc: string | null): string {
  if (!doc) return '';
  const digits = doc.replace(/\D/g, '');
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc;
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

export function SgpOperationsPanel({ customer, onClose }: SgpOperationsPanelProps) {
  const { user } = useAuth();
  const [tab, setTab] = useState<OperationTab>('financial');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; title: string; data: unknown } | null>(null);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState(customer.companyId || '');

  useEffect(() => {
    getCompanies().then((list) => {
      setCompanies(list);
      if (!companyId && list.length > 0) setCompanyId(list[0].id);
    }).catch(() => {});
  }, []);

  const [contractId, setContractId] = useState(customer.externalId || '');
  const [docNumber, setDocNumber] = useState(customer.document || '');
  const [invoiceId, setInvoiceId] = useState('');
  const [sendChannel, setSendChannel] = useState<'email' | 'sms'>('email');
  const [serviceId, setServiceId] = useState('');
  const [username, setUsername] = useState('');
  const [ticketContent, setTicketContent] = useState('');
  const [occurrenceTypeId, setOccurrenceTypeId] = useState(1);
  const [radiusUsername, setRadiusUsername] = useState('');
  const [radiusResult, setRadiusResult] = useState<Record<string, unknown> | null>(null);

  const [authId, setAuthId] = useState('');
  const [settleAmount, setSettleAmount] = useState('');
  const [settleMethod, setSettleMethod] = useState('');
  const [reverseReason, setReverseReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [contractStatusId, setContractStatusId] = useState(1);
  const [contractReason, setContractReason] = useState('');
  const [oltId, setOltId] = useState('');
  const [ponId, setPonId] = useState('');
  const [onuSerial, setOnuSerial] = useState('');
  const [onuId, setOnuId] = useState('');
  const [deauthReason, setDeauthReason] = useState('');
  const [stockItemId, setStockItemId] = useState('');
  const [stockReason, setStockReason] = useState('');
  const [cardId, setCardId] = useState('');
  const [recurringCardId, setRecurringCardId] = useState('');
  const [recurringReason, setRecurringReason] = useState('');
  const [genericServiceId, setGenericServiceId] = useState('');
  const [genericReason, setGenericReason] = useState('');
  const [docId, setDocId] = useState('');
  const [docReason, setDocReason] = useState('');

  const exec = async (fn: () => Promise<unknown>, title: string) => {
    setLoading(true);
    setResult(null);
    try {
      const data = await fn();
      setResult({ ok: true, title, data });
    } catch (err: any) {
      setResult({ ok: false, title: 'Erro', data: err.message || 'Falha na operação' });
    } finally {
      setLoading(false);
    }
  };

  const needAuth = (op: string) => {
    if (!authId.trim()) {
      setResult({ ok: false, title: 'Erro', data: `Operação "${op}" requer uma autorização aprovada. Informe o ID da autorização.` });
      return false;
    }
    return true;
  };

  const renderResult = () => {
    if (!result) return null;
    const r = result.data as any;
    return (
      <div className={`operation-result ${result.ok ? 'success' : 'error'}`}>
        <div className="result-header">
          <span>{result.ok ? '✅' : '❌'}</span>
          <strong>{result.title}</strong>
          <button className="modal-close" onClick={() => setResult(null)}>×</button>
        </div>
        <div className="result-body">
          {r?.message && <p>{r.message}</p>}
          {r?.protocol && <div className="detail-row"><span className="label">Protocolo</span><span className="value mono">{r.protocol}</span></div>}
          {r?.copyPasteCode && (
            <div className="pix-code-box">
              <span className="pix-label">Código PIX copia-e-cola</span>
              <code className="pix-code" onClick={() => copyToClipboard(r.copyPasteCode)}>{r.copyPasteCode}</code>
              <span className="pix-hint">Clique para copiar</span>
            </div>
          )}
          {r?.qrCode && <div className="detail-row"><span className="label">QR Code</span><span className="value"><img src={r.qrCode} alt="PIX QR" style={{ maxWidth: 200, borderRadius: 8 }} /></span></div>}
          {Array.isArray(r?.invoices) && r.invoices.map((inv: any, i: number) => (
            <div key={i} className="sgp-card" style={{ marginTop: 8 }}>
              <div className="sgp-card-row"><span>Valor</span><strong>R$ {inv.amount || '—'}</strong></div>
              <div className="sgp-card-row"><span>Vencimento</span><span>{inv.dueDate || '—'}</span></div>
              {inv.digitableLine && <div className="sgp-card-row"><span>Linha digitável</span><code style={{ fontSize: 11, maxWidth: '60%', wordBreak: 'break-all' }}>{inv.digitableLine}</code></div>}
              {inv.paymentUrl && <div className="sgp-card-row"><a href={inv.paymentUrl} target="_blank" rel="noopener noreferrer">Abrir boleto</a></div>}
            </div>
          ))}
          {r?.error && <p className="error-text">{r.error}</p>}
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Operações SGP — {customer.name || 'Cliente'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="sgp-ops-tabs">
          <button className={`sgp-ops-tab${tab === 'financial' ? ' active' : ''}`} onClick={() => setTab('financial')}>Financeiro</button>
          <button className={`sgp-ops-tab${tab === 'technical' ? ' active' : ''}`} onClick={() => setTab('technical')}>Técnico</button>
          <button className={`sgp-ops-tab${tab === 'critical' ? ' active' : ''}`} onClick={() => setTab('critical')}>Crítico/Destrutivo</button>
          <button className={`sgp-ops-tab${tab === 'radius' ? ' active' : ''}`} onClick={() => setTab('radius')}>RADIUS</button>
        </div>

        <div className="modal-body">
          {customer.document && (
            <div className="sgp-doc-badge">Cliente: <strong>{customer.name}</strong> • Doc: {formatCpfCnpj(customer.document)}</div>
          )}
          {renderResult()}

          {/* ═══════ FINANCIAL (SAFE) ═══════ */}
          {tab === 'financial' && (
            <div className="sgp-ops-grid">
              <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); exec(() => sgpInvoiceSecondCopy({ contractId, documentNumber: docNumber }), '2ª Via de Boleto'); }}>
                <h3>📄 Segunda via de boleto</h3>
                <label>Contrato <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="ID do contrato" /></label>
                <label>Nº Documento <input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} placeholder="Número do documento" /></label>
                <button type="submit" disabled={loading || !contractId || !docNumber}>{loading ? '...' : 'Gerar 2ª via'}</button>
              </form>

              <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); exec(() => sgpInvoicePix({ contractId, invoiceId }), 'PIX Gerado'); }}>
                <h3>💸 Gerar PIX</h3>
                <label>Contrato <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="ID do contrato" /></label>
                <label>ID da Fatura <input value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} placeholder="ID da fatura no SGP" /></label>
                <button type="submit" disabled={loading || !contractId || !invoiceId}>{loading ? '...' : 'Gerar PIX'}</button>
              </form>

              <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); exec(() => sgpInvoiceSend({ contractId, documentNumber: docNumber, channel: sendChannel }), 'Fatura enviada'); }}>
                <h3>📧 Enviar fatura</h3>
                <label>Contrato <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="ID do contrato" /></label>
                <label>Nº Documento <input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} placeholder="Número do documento" /></label>
                <label>Canal
                  <select value={sendChannel} onChange={(e) => setSendChannel(e.target.value as 'email' | 'sms')}>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                  </select>
                </label>
                <button type="submit" disabled={loading || !contractId || !docNumber}>{loading ? '...' : 'Enviar fatura'}</button>
              </form>
            </div>
          )}

          {/* ═══════ TECHNICAL (SAFE) ═══════ */}
          {tab === 'technical' && (
            <div className="sgp-ops-grid">
              <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); exec(() => sgpRebootCpe({ serviceId }), 'Reboot CPE'); }}>
                <h3>🔄 Reiniciar CPE</h3>
                <p className="sgp-ops-desc">Reinicia o modem/CPE do cliente remotamente.</p>
                <label>ID do Serviço <input value={serviceId} onChange={(e) => setServiceId(e.target.value)} placeholder="ID do serviço no SGP" /></label>
                <button type="submit" className="danger" disabled={loading || !serviceId}>{loading ? '...' : 'Reiniciar CPE'}</button>
              </form>

              <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); exec(() => sgpDisconnectRadius({ username }), 'Desconexão RADIUS'); }}>
                <h3>⛔ Desconectar RADIUS</h3>
                <p className="sgp-ops-desc">Força a desconexão da sessão PPPoE do cliente.</p>
                <label>Usuário PPPoE <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="login PPPoE" /></label>
                <button type="submit" className="danger" disabled={loading || !username}>{loading ? '...' : 'Desconectar'}</button>
              </form>

              <form className="sgp-ops-card sgp-ops-card-wide" onSubmit={(e) => { e.preventDefault(); exec(() => sgpCreateTicket({ contractId, content: ticketContent, occurrenceTypeId }), 'Ticket SGP criado'); }}>
                <h3>🎫 Criar ticket no SGP</h3>
                <p className="sgp-ops-desc">Abre um chamado diretamente no sistema SGP.</p>
                <label>Contrato <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="ID do contrato" /></label>
                <label>Tipo de Ocorrência <input type="number" value={occurrenceTypeId} onChange={(e) => setOccurrenceTypeId(Number(e.target.value))} placeholder="ID do tipo (ex: 1)" /></label>
                <label>Descrição <textarea value={ticketContent} onChange={(e) => setTicketContent(e.target.value)} placeholder="Descreva o problema..." rows={3} /></label>
                <button type="submit" disabled={loading || !contractId || !ticketContent}>{loading ? '...' : 'Criar ticket'}</button>
              </form>
            </div>
          )}

          {/* ═══════ CRITICAL / DESTRUCTIVE ═══════ */}
          {tab === 'critical' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="sgp-doc-badge" style={{ background: 'var(--color-warning-bg, #fff3cd)', borderColor: 'var(--color-warning, #ffc107)' }}>
                Operações críticas/destrutivas. Requerem uma <strong>autorização aprovada</strong> pelo gestor.
              </div>
              <label style={{ maxWidth: 400 }}>
                ID da Autorização *
                <input value={authId} onChange={(e) => setAuthId(e.target.value)} placeholder="Cole o ID da autorização aprovada" />
              </label>

              <h3 style={{ margin: '8px 0 0' }}>Financeiro Crítico</h3>
              <div className="sgp-ops-grid">
                <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); if (!needAuth('Liquidar')) return; exec(() => sgpSettleInvoice({ invoiceId, amount: Number(settleAmount) || 0, paymentDate: settleMethod || new Date().toISOString().split('T')[0], authorizationId: authId }), 'Fatura liquidada'); }}>
                  <h3>💰 Liquidar fatura</h3>
                  <label>ID Fatura <input value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} placeholder="ID da fatura" /></label>
                  <label>Valor (centavos) <input type="number" value={settleAmount} onChange={(e) => setSettleAmount(e.target.value)} placeholder="Ex: 5000 = R$ 50,00" /></label>
                  <label>Data pagamento <input type="date" value={settleMethod} onChange={(e) => setSettleMethod(e.target.value)} /></label>
                  <button type="submit" disabled={loading || !invoiceId || !authId}>{loading ? '...' : 'Liquidar'}</button>
                </form>

                <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); if (!needAuth('Estornar')) return; exec(() => sgpReverseInvoice({ invoiceId, reason: reverseReason, authorizationId: authId }), 'Fatura estornada'); }}>
                  <h3>↩️ Estornar fatura</h3>
                  <label>ID Fatura <input value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} placeholder="ID da fatura" /></label>
                  <label>Motivo <input value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} placeholder="Motivo do estorno" /></label>
                  <button type="submit" className="danger" disabled={loading || !invoiceId || !reverseReason || !authId}>{loading ? '...' : 'Estornar'}</button>
                </form>

                <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); if (!needAuth('Cancelar')) return; exec(() => sgpCancelInvoice({ invoiceId, reason: cancelReason, authorizationId: authId }), 'Fatura cancelada'); }}>
                  <h3>🚫 Cancelar fatura</h3>
                  <label>ID Fatura <input value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} placeholder="ID da fatura" /></label>
                  <label>Motivo <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Motivo do cancelamento" /></label>
                  <button type="submit" className="danger" disabled={loading || !invoiceId || !cancelReason || !authId}>{loading ? '...' : 'Cancelar'}</button>
                </form>

                <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); if (!needAuth('Excluir cartão')) return; exec(() => sgpDeleteCard({ cardId, authorizationId: authId }), 'Cartão excluído'); }}>
                  <h3>💳 Excluir cartão</h3>
                  <label>ID Cartão <input value={cardId} onChange={(e) => setCardId(e.target.value)} placeholder="ID do cartão no SGP" /></label>
                  <button type="submit" className="danger" disabled={loading || !cardId || !authId}>{loading ? '...' : 'Excluir cartão'}</button>
                </form>

                <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); if (!needAuth('Excluir cobrança recorrente')) return; exec(() => sgpDeleteRecurringCharge({ cardId: recurringCardId, reason: recurringReason, authorizationId: authId }), 'Cobrança recorrente excluída'); }}>
                  <h3>🔁 Excluir cobrança recorrente</h3>
                  <label>ID Cartão <input value={recurringCardId} onChange={(e) => setRecurringCardId(e.target.value)} placeholder="ID do cartão" /></label>
                  <label>Motivo <input value={recurringReason} onChange={(e) => setRecurringReason(e.target.value)} placeholder="Motivo" /></label>
                  <button type="submit" className="danger" disabled={loading || !recurringCardId || !recurringReason || !authId}>{loading ? '...' : 'Excluir recorrência'}</button>
                </form>
              </div>

              <h3 style={{ margin: '8px 0 0' }}>Técnico Crítico</h3>
              <div className="sgp-ops-grid">
                <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); if (!needAuth('Atualizar contrato')) return; exec(() => sgpUpdateContractStatus({ contractId, statusId: contractStatusId, reason: contractReason, authorizationId: authId }), 'Status atualizado'); }}>
                  <h3>📝 Atualizar status do contrato</h3>
                  <label>Contrato <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="ID do contrato" /></label>
                  <label>Status <input type="number" value={contractStatusId} onChange={(e) => setContractStatusId(Number(e.target.value))} placeholder="1=Ativo, 2=Suspenso, 3=Cancelado" /></label>
                  <label>Motivo <input value={contractReason} onChange={(e) => setContractReason(e.target.value)} placeholder="Motivo da alteração" /></label>
                  <button type="submit" disabled={loading || !contractId || !authId}>{loading ? '...' : 'Atualizar status'}</button>
                </form>

                <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); if (!needAuth('Autorizar ONU')) return; exec(() => sgpAuthorizeOnu({ oltId, ponId, onuSerial, authorizationId: authId }), 'ONU autorizada'); }}>
                  <h3>📡 Autorizar ONU</h3>
                  <label>OLT <input value={oltId} onChange={(e) => setOltId(e.target.value)} placeholder="ID da OLT" /></label>
                  <label>PON <input value={ponId} onChange={(e) => setPonId(e.target.value)} placeholder="ID da porta PON" /></label>
                  <label>Serial ONU <input value={onuSerial} onChange={(e) => setOnuSerial(e.target.value)} placeholder="Serial da ONU" /></label>
                  <button type="submit" disabled={loading || !oltId || !ponId || !onuSerial || !authId}>{loading ? '...' : 'Autorizar ONU'}</button>
                </form>

                <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); if (!needAuth('Deautorizar ONU')) return; exec(() => sgpDeauthorizeOnu({ onuId, reason: deauthReason, authorizationId: authId }), 'ONU removida'); }}>
                  <h3>🚫 Remover ONU</h3>
                  <label>ID ONU <input value={onuId} onChange={(e) => setOnuId(e.target.value)} placeholder="ID da ONU no SGP" /></label>
                  <label>Motivo <input value={deauthReason} onChange={(e) => setDeauthReason(e.target.value)} placeholder="Motivo da remoção" /></label>
                  <button type="submit" className="danger" disabled={loading || !onuId || !deauthReason || !authId}>{loading ? '...' : 'Remover ONU'}</button>
                </form>

                <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); if (!needAuth('Estorno estoque')) return; exec(() => sgpReverseStock({ stockItemId, reason: stockReason, authorizationId: authId }), 'Estorno estoque realizado'); }}>
                  <h3>📦 Estorno de estoque</h3>
                  <label>ID Lançamento <input value={stockItemId} onChange={(e) => setStockItemId(e.target.value)} placeholder="ID do item de lançamento" /></label>
                  <label>Motivo <input value={stockReason} onChange={(e) => setStockReason(e.target.value)} placeholder="Motivo do estorno" /></label>
                  <button type="submit" className="danger" disabled={loading || !stockItemId || !stockReason || !authId}>{loading ? '...' : 'Estornar estoque'}</button>
                </form>

                <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); if (!needAuth('Excluir serviço')) return; exec(() => sgpDeleteGenericService({ serviceId: genericServiceId, reason: genericReason, authorizationId: authId }), 'Serviço excluído'); }}>
                  <h3>🔧 Excluir serviço genérico</h3>
                  <label>ID Serviço <input value={genericServiceId} onChange={(e) => setGenericServiceId(e.target.value)} placeholder="ID do serviço" /></label>
                  <label>Motivo <input value={genericReason} onChange={(e) => setGenericReason(e.target.value)} placeholder="Motivo" /></label>
                  <button type="submit" className="danger" disabled={loading || !genericServiceId || !genericReason || !authId}>{loading ? '...' : 'Excluir serviço'}</button>
                </form>

                <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); if (!needAuth('Remover documento')) return; exec(() => sgpDeleteCustomerDocument({ documentId: docId, reason: docReason, authorizationId: authId }), 'Documento removido'); }}>
                  <h3>📄 Remover documento do cliente</h3>
                  <label>ID Documento <input value={docId} onChange={(e) => setDocId(e.target.value)} placeholder="ID do documento" /></label>
                  <label>Motivo <input value={docReason} onChange={(e) => setDocReason(e.target.value)} placeholder="Motivo" /></label>
                  <button type="submit" className="danger" disabled={loading || !docId || !docReason || !authId}>{loading ? '...' : 'Remover documento'}</button>
                </form>
              </div>
            </div>
          )}

          {/* ═══════ RADIUS ═══════ */}
          {tab === 'radius' && (
            <div>
              <form className="sgp-ops-card" onSubmit={(e) => { e.preventDefault(); exec(() => testRadius(radiusUsername, companyId), 'Consulta RADIUS'); }} style={{ maxWidth: 500, margin: '0 auto' }}>
                <h3>📡 Teste de Sessão RADIUS</h3>
                <p className="sgp-ops-desc">Consulta a sessão PPPoE atual do cliente no RADIUS da empresa selecionada.</p>
                {companies.length > 1 && (
                  <label>Empresa
                    <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </label>
                )}
                <label>Usuário PPPoE <input value={radiusUsername} onChange={(e) => setRadiusUsername(e.target.value)} placeholder="login do cliente" /></label>
                <button type="submit" disabled={loading || !radiusUsername || !companyId}>{loading ? 'Consultando...' : 'Consultar sessão'}</button>
              </form>

              {radiusResult && (
                <div className="radius-result">
                  <h3>📊 Resultado da consulta</h3>
                  <div className="sgp-cards">
                    {(radiusResult as any).mapped ? (
                      <>
                        {/* Dados do cliente */}
                        {(radiusResult as any).rawSessionSample && (
                          <div className="sgp-card" style={{ borderLeft: '3px solid var(--color-primary, #0d6efd)' }}>
                            <div className="sgp-card-title">👤 Dados do Cliente</div>
                            <div className="sgp-card-row"><span>Nome</span><strong>{(radiusResult as any).rawSessionSample.nome || '—'}</strong></div>
                            <div className="sgp-card-row"><span>Login PPPoE</span><span className="mono">{(radiusResult as any).rawSessionSample.pppoe_login || (radiusResult as any).mapped.username || '—'}</span></div>
                            <div className="sgp-card-row"><span>Plano</span><span>{(radiusResult as any).rawSessionSample.plano || '—'}</span></div>
                            <div className="sgp-card-row"><span>Endereço</span><span>{(radiusResult as any).rawSessionSample.endereco || '—'}</span></div>
                            <div className="sgp-card-row"><span>Cidade/UF</span><span>{(radiusResult as any).rawSessionSample.endereco_cidade ? `${(radiusResult as any).rawSessionSample.endereco_cidade}/${(radiusResult as any).rawSessionSample.endereco_uf || ''}` : '—'}</span></div>
                            <div className="sgp-card-row"><span>Serviço ID</span><span className="mono">{(radiusResult as any).rawSessionSample.servico_id || '—'}</span></div>
                          </div>
                        )}

                        {/* Dados da sessão */}
                        <div className="sgp-card" style={{ borderLeft: `3px solid ${(radiusResult as any).mapped.online ? 'var(--color-success, #198754)' : 'var(--color-neutral, #6c757d)'}` }}>
                          <div className="sgp-card-title">📡 Sessão RADIUS</div>
                          <div className="sgp-card-row">
                            <span>Status</span>
                            <span className={`pill ${(radiusResult as any).mapped.online ? 'success' : 'neutral'}`}>
                              {(radiusResult as any).mapped.online ? '🟢 Online' : '⚪ Offline'}
                            </span>
                          </div>
                          <div className="sgp-card-row"><span>IP atribuído</span><span className="mono">{(radiusResult as any).mapped.framedIpAddress || '—'}</span></div>
                          <div className="sgp-card-row"><span>MAC</span><span className="mono">{(radiusResult as any).mapped.callingStationId || '—'}</span></div>
                          <div className="sgp-card-row"><span>NAS</span><span>{(radiusResult as any).mapped.nas || '—'}</span></div>
                          <div className="sgp-card-row"><span>Porta</span><span>{(radiusResult as any).mapped.nasPortId || '—'}</span></div>
                          <div className="sgp-card-row"><span>Tempo online</span><strong>{(radiusResult as any).mapped.sessionTime || '—'}</strong></div>
                          <div className="sgp-card-row"><span>Início da sessão</span><span>{(radiusResult as any).mapped.startTime ? new Date((radiusResult as any).mapped.startTime).toLocaleString('pt-BR') : '—'}</span></div>
                          <div className="sgp-card-row">
                            <span>Download</span>
                            <span>{(radiusResult as any).mapped.inputOctets != null ? `${((radiusResult as any).mapped.inputOctets / 1024 / 1024 / 1024).toFixed(2)} GB` : '—'}</span>
                          </div>
                          <div className="sgp-card-row">
                            <span>Upload</span>
                            <span>{(radiusResult as any).mapped.outputOctets != null ? `${((radiusResult as any).mapped.outputOctets / 1024 / 1024 / 1024).toFixed(2)} GB` : '—'}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="sgp-card">
                        <div className="sgp-card-row"><span>Status</span><span className={`pill ${(radiusResult as any).ok ? 'success' : 'danger'}`}>{(radiusResult as any).ok ? 'OK' : 'Falha'}</span></div>
                        <div className="sgp-card-row"><span>Mensagem</span><span>{(radiusResult as any).message || (radiusResult as any).error || '—'}</span></div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
